import React, { useEffect } from "react";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import NotoSansKannada from "./NotoSansKannada";
import helperServices from "../../../shared/helper/helper";

function PrintAdmissionConsent(props) {
  const { patientInfo, regDate } = props;

  const name =
    patientInfo?.fullName ||
    [patientInfo?.fname, patientInfo?.mname, patientInfo?.lname]
      .filter(Boolean)
      .join(" ") ||
    "—";

  useEffect(() => {
    props.parentCallBack();
    printConsent();
  }, []);

  const buildDocDefinition = () => {
    const field = (val) => val || "—";

    // ---------------------------------------------------------
    // PATIENT INFORMATION TABLE
    //
    // Admission Consent intentionally does NOT display:
    // IP No / Ward / Diagnosis.
    // This preserves the existing Admission PDF behavior.
    // ---------------------------------------------------------
    const patientTable = (lang) => {
      const isKn = lang === "kn";
      const kn = isKn ? "NotoSansKannada" : "Roboto";

      return {
        table: {
          widths: ["40%", "30%", "30%"],

          body: [
            [
              {
                text: isKn
                  ? `ರೋಗಿಯ ಹೆಸರು : ${name}`
                  : `Patient Name : ${name}`,
                bold: true,
                fontSize: 10,
                font: kn,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },

              {
                text: isKn
                  ? `ವಯಸ್ಸು : ${field(patientInfo?.age)}`
                  : `Age : ${field(patientInfo?.age)}`,
                bold: true,
                fontSize: 10,
                font: kn,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },

              {
                text: isKn
                  ? `ಲಿಂಗ : ${field(patientInfo?.gender)}`
                  : `Gender : ${field(patientInfo?.gender)}`,
                bold: true,
                fontSize: 10,
                font: kn,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },
            ],

            [
              {
                text: isKn
                  ? `ಯು ಹೆಚ್ ಐ ಡಿ : ${field(patientInfo?.id)}`
                  : `UHID : ${field(patientInfo?.id)}`,
                bold: true,
                fontSize: 10,
                font: kn,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },

              {
                text: isKn
                  ? `ಚಿಕಿತ್ಸಿಸುವ ವೈದ್ಯರ ಹೆಸರು : ${field(
                      patientInfo?.doctorName
                    )}`
                  : `Treating Doctor : ${field(
                      patientInfo?.doctorName
                    )}`,
                bold: true,
                fontSize: 10,
                font: kn,
                colSpan: 2,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },

              {},
            ],
          ],
        },

        margin: [0, 0, 0, 10],
      };
    };

    // ---------------------------------------------------------
    // GUARDIAN SECTION
    //
    // Render only when guardian information/signature exists.
    // ---------------------------------------------------------
    const guardianTable = (lang) => {
      const isKn = lang === "kn";
      const kn = isKn ? "NotoSansKannada" : "Roboto";

      const guardianName = patientInfo?.guardianName;
      const guardianRelation = patientInfo?.guardianRelation;
      const guardianSignature = patientInfo?.guardianSignature;
      const guardianSignedDate = patientInfo?.guardianSignedDate;
      const guardianSignedTime = patientInfo?.guardianSignedTime;

      if (!guardianName && !guardianRelation && !guardianSignature) {
        return null;
      }

      return {
        stack: [
          {
            text: isKn
              ? "ರೋಗಿಗೆ ಸಹಿ ಮಾಡಲಾಗದಿದ್ದಲ್ಲಿ ಅಥವಾ ಅಪ್ರಾಪ್ತನಾಗಿದ್ದಲ್ಲಿ"
              : "If the patient cannot sign or minor",
            bold: true,
            fontSize: 10,
            font: kn,
            margin: [0, 0, 0, 4],
          },

          {
            text: isKn
              ? "ಸಂಗಾತಿ / ಪೋಷಕರ / ಸಂಬಂಧಿಕರ ಸಹಿ :"
              : "Spouse, parent or guardian :",
            bold: true,
            fontSize: 10,
            font: kn,
            margin: [0, 0, 0, 4],
          },

          {
            text: isKn
              ? "ರೋಗಿಯ ಜೊತೆಗಿನ ಸಂಬಂಧ :"
              : "Relationship with patient :",
            bold: true,
            fontSize: 10,
            font: kn,
            margin: [0, 0, 0, 6],
          },

          {
            table: {
              widths: ["8%", "27%", "25%", "20%", "20%"],

              body: [
                [
                  {
                    text: "",
                    border: [true, true, true, true],
                  },

                  {
                    text: isKn ? "ಹೆಸರು" : "Name",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },

                  {
                    text: isKn ? "ಸಂಬಂಧ" : "Relationship",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },

                  {
                    text: isKn ? "ಸಹಿ" : "Signature",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },

                  {
                    text: isKn ? "ದಿನಾಂಕ / ಸಮಯ" : "Date / Time",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },
                ],

                [
                  {
                    text: "1.",
                    fontSize: 10,
                    alignment: "center",
                    border: [true, true, true, true],
                    margin: [2, 8, 2, 8],
                  },

                  {
                    text: field(guardianName),
                    fontSize: 10,
                    font: kn,
                    border: [true, true, true, true],
                    margin: [2, 8, 2, 8],
                  },

                  {
                    text: field(guardianRelation),
                    fontSize: 10,
                    font: kn,
                    border: [true, true, true, true],
                    margin: [2, 8, 2, 8],
                  },

                  guardianSignature
                    ? {
                        image: guardianSignature,
                        width: 90,
                        height: 40,
                        alignment: "center",
                        border: [true, true, true, true],
                        margin: [2, 5, 2, 5],
                      }
                    : {
                        text: "—",
                        fontSize: 10,
                        alignment: "center",
                        border: [true, true, true, true],
                        margin: [2, 8, 2, 8],
                      },

                  {
                    text: `${guardianSignedDate || "—"}\n${
                      guardianSignedTime || "—"
                    }`,
                    fontSize: 9,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                    margin: [2, 6, 2, 6],
                  },
                ],
              ],
            },
          },
        ],

        margin: [0, 4, 0, 10],
      };
    };

    // ---------------------------------------------------------
    // WITNESS SECTION
    //
    // Render only when witnesses exist.
    // ---------------------------------------------------------
    const witnessTable = (lang) => {
      const isKn = lang === "kn";
      const kn = isKn ? "NotoSansKannada" : "Roboto";

      const witnesses = Array.isArray(patientInfo?.witnesses)
        ? patientInfo.witnesses
        : [];

      if (witnesses.length === 0) {
        return null;
      }

      return {
        stack: [
          {
            text: isKn ? "ಸಾಕ್ಷಿ" : "Witness",
            bold: true,
            fontSize: 10,
            font: kn,
            margin: [0, 0, 0, 6],
          },

          {
            table: {
              widths: ["8%", "27%", "27%", "19%", "19%"],

              body: [
                [
                  {
                    text: "",
                    border: [true, true, true, true],
                  },

                  {
                    text: isKn ? "ಹೆಸರು" : "Name",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },

                  {
                    text: isKn ? "ಸಂಬಂಧ" : "Relationship",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },

                  {
                    text: isKn ? "ಸಹಿ" : "Signature",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },

                  {
                    text: isKn ? "ದಿನಾಂಕ / ಸಮಯ" : "Date / Time",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },
                ],

                ...witnesses.map((w, index) => {
                  const signedDate = w.signedAt
                    ? helperServices.formatDateOnly(w.signedAt, true)
                    : w.signedDate || "—";

                  const signedTime = w.signedAt
                    ? helperServices.formatTimeOnly(w.signedAt)
                    : w.signedTime || "—";

                  return [
                    {
                      text: `${index + 1}.`,
                      fontSize: 10,
                      border: [true, true, true, true],
                      alignment: "center",
                      margin: [2, 8, 2, 8],
                    },

                    {
                      text: field(w.name),
                      fontSize: 10,
                      font: kn,
                      border: [true, true, true, true],
                      margin: [2, 8, 2, 8],
                    },

                    {
                      text: field(w.relation),
                      fontSize: 10,
                      font: kn,
                      border: [true, true, true, true],
                      margin: [2, 8, 2, 8],
                    },

                    w.signature
                      ? {
                          image: w.signature,
                          width: 80,
                          height: 35,
                          alignment: "center",
                          border: [true, true, true, true],
                          margin: [2, 5, 2, 5],
                        }
                      : {
                          text: "—",
                          fontSize: 10,
                          alignment: "center",
                          border: [true, true, true, true],
                          margin: [2, 8, 2, 8],
                        },

                    {
                      text: `${signedDate}\n${signedTime}`,
                      fontSize: 9,
                      font: kn,
                      alignment: "center",
                      border: [true, true, true, true],
                      margin: [2, 6, 2, 6],
                    },
                  ];
                }),
              ],
            },
          },
        ],

        margin: [0, 4, 0, 10],
      };
    };

    // ---------------------------------------------------------
    // REQUIRED GUARDIAN / WITNESS SECTION
    // ---------------------------------------------------------
    const requiredGuardianWitnessTable = (lang) => {
      const isKn = lang === "kn";
      const font = isKn ? "NotoSansKannada" : "Roboto";
      const hasGuardianData = Boolean(guardianTable(lang));
      const hasWitnessData = Boolean(witnessTable(lang));
      const participants = [];

      if (hasGuardianData) {
        participants.push({
          name: patientInfo?.guardianName,
          signature: patientInfo?.guardianSignature,
          relation: patientInfo?.guardianRelation,
        });
      }

      if (hasWitnessData) {
        participants.push(
          ...patientInfo.witnesses.map((witness) => ({
            name: witness?.name,
            signature: witness?.signature,
            relation: witness?.relation,
          })),
        );
      }

      while (participants.length < 2) {
        participants.push({ name: "", signature: null, relation: "" });
      }

      return {
        unbreakable: true,
        stack: [
          {
            text: isKn
              ? "ರೋಗಿಗೆ ಸಹಿ ಮಾಡಲಾಗದಿದ್ದಲ್ಲಿ ಅಥವಾ ಅಪ್ರಾಪ್ತನಾಗಿದ್ದಲ್ಲಿ"
              : "If the patient cannot sign or minor",
            bold: true,
            fontSize: 10,
            font,
            margin: [0, 0, 0, 4],
          },
          {
            text: isKn
              ? "ಸಂಗಾತಿ / ಪೋಷಕರ / ಸಂಬಂಧಿಕರ ಸಹಿ :"
              : "Spouse/Parent/Guardian sign :",
            bold: true,
            fontSize: 10,
            font,
            margin: [0, 0, 0, 6],
          },
          {
            table: {
              widths: ["7%", "33%", "27%", "33%"],
              body: [
                [
                  { text: "", border: [true, true, true, true] },
                  {
                    text: isKn ? "ಸಾಕ್ಷಿಯ ಹೆಸರು" : "Name of Witness",
                    bold: true,
                    fontSize: 10,
                    font,
                    alignment: "center",
                  },
                  {
                    text: isKn ? "ಸಹಿ" : "Signature",
                    bold: true,
                    fontSize: 10,
                    font,
                    alignment: "center",
                  },
                  {
                    text: isKn
                      ? "ರೋಗಿಯ ಜೊತೆಗಿನ ಸಂಬಂಧ"
                      : "Relationship with Patient",
                    bold: true,
                    fontSize: 10,
                    font,
                    alignment: "center",
                  },
                ],
                ...participants.map((participant, index) => [
                  {
                    text: `${index + 1}.`,
                    fontSize: 10,
                    alignment: "center",
                    margin: [2, 9, 2, 9],
                  },
                  {
                    text: participant.name || "",
                    fontSize: 10,
                    font,
                    margin: [2, 9, 2, 9],
                  },
                  participant.signature
                    ? {
                        image: participant.signature,
                        fit: [90, 35],
                        alignment: "center",
                        margin: [2, 4, 2, 4],
                      }
                    : { text: "", margin: [2, 9, 2, 9] },
                  {
                    text: participant.relation || "",
                    fontSize: 10,
                    font,
                    margin: [2, 9, 2, 9],
                  },
                ]),
              ],
            },
          },
        ],
        margin: [0, 4, 0, 10],
      };
    };

    // ---------------------------------------------------------
    // SIGNATURE BLOCK
    //
    // Patient/Doctor signatures are displayed side-by-side.
    // ---------------------------------------------------------
    const sigBlock = (lang) => {
      const isKn = lang === "kn";
      const kn = isKn ? "NotoSansKannada" : "Roboto";

      const patientSignature = patientInfo?.patientSignature;
      const doctorSignature = patientInfo?.doctorSignature;

      return {
        columns: [
          {
            width: "50%",

            stack: [
              {
                text: isKn
                  ? "ರೋಗಿಯ ಸಹಿ :"
                  : "Patient Signature :",
                bold: true,
                fontSize: 10,
                font: kn,
              },

              patientSignature
                ? {
                    image: patientSignature,
                    width: 120,
                    height: 45,
                    margin: [0, 5, 0, 5],
                  }
                : {
                    text: " ",
                    fontSize: 10,
                    margin: [0, 12, 0, 0],
                  },

              {
                text: isKn
                  ? `ದಿನಾಂಕ : ${
                      patientInfo?.patientSignedDate || "—"
                    }`
                  : `Date : ${
                      patientInfo?.patientSignedDate || "—"
                    }`,
                bold: true,
                fontSize: 10,
                font: kn,
              },

              {
                text: isKn
                  ? `ಸಮಯ : ${
                      patientInfo?.patientSignedTime || "—"
                    }`
                  : `Time : ${
                      patientInfo?.patientSignedTime || "—"
                    }`,
                bold: true,
                fontSize: 10,
                font: kn,
                margin: [0, 4, 0, 0],
              },
            ],
          },

          {
            width: "50%",

            stack: [
              {
                text: isKn
                  ? `ವೈದ್ಯರ ಹೆಸರು ಮತ್ತು ಸಹಿ : ${field(
                      patientInfo?.doctorName
                    )}`
                  : `Name and Signature of Treating Doctor : ${field(
                      patientInfo?.doctorName
                    )}`,
                bold: true,
                fontSize: 10,
                font: kn,
              },

              doctorSignature
                ? {
                    image: doctorSignature,
                    width: 120,
                    height: 45,
                    margin: [0, 5, 0, 5],
                  }
                : {
                    text: " ",
                    fontSize: 10,
                    margin: [0, 12, 0, 0],
                  },

              {
                text: isKn
                  ? `ದಿನಾಂಕ : ${
                      patientInfo?.doctorSignedDate || "—"
                    }`
                  : `Date : ${
                      patientInfo?.doctorSignedDate || "—"
                    }`,
                bold: true,
                fontSize: 10,
                font: kn,
              },

              {
                text: isKn
                  ? `ಸಮಯ : ${
                      patientInfo?.doctorSignedTime || "—"
                    }`
                  : `Time : ${
                      patientInfo?.doctorSignedTime || "—"
                    }`,
                bold: true,
                fontSize: 10,
                font: kn,
                margin: [0, 4, 0, 0],
              },
            ],
          },
        ],

        margin: [0, 8, 0, 10],
      };
    };

    // ---------------------------------------------------------
    // DIVIDER
    // ---------------------------------------------------------
    const divider = {
      canvas: [
        {
          type: "line",
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 1,
          lineColor: "#000000",
        },
      ],

      margin: [0, 8, 0, 8],
    };

    // ---------------------------------------------------------
    // DOCUMENT
    // ---------------------------------------------------------
    return {
      pageSize: "A4",
      pageMargins: [30, 30, 30, 30],

      defaultStyle: {
        font: "Roboto",
      },

      content: [
        // =====================================================
        // ENGLISH
        // =====================================================

        {
          text: "ADMISSION CONSENT",
          fontSize: 14,
          bold: true,
          alignment: "center",
          margin: [0, 0, 0, 12],
        },

        patientTable("en"),

        {
          text: "I voluntarily consent to the procedures and services that may be performed for me on an inpatient basis under the general and special instructions of my physician, and/or his/her assistant or designee. I understand that these procedures and services may include but are not limited to emergency treatment or services, laboratory procedures, imaging services, medical or surgical treatment or procedures, anesthesia or hospital services. I understand that other conditions may be diagnosed which may require additional treatment.",

          fontSize: 10,

          margin: [0, 0, 0, 8],
        },

        {
          text: "I acknowledge that no guarantees have been made to me as to the result of any treatment. These things have been fully explained to me in my vernacular language and I have understood the same.",

          fontSize: 10,

          margin: [0, 0, 0, 8],
        },

        sigBlock("en"),

        requiredGuardianWitnessTable("en"),

        divider,

        // =====================================================
        // KANNADA
        // =====================================================

        {
          text: "ದಾಖಲಾತಿಗೆ ಅನುಮತಿ ಪತ್ರ",
          pageBreak: "before",
          fontSize: 14,
          bold: true,
          alignment: "center",
          font: "NotoSansKannada",
          margin: [0, 0, 0, 12],
        },

        patientTable("kn"),

        {
          text: "ನನ್ನ ವೈದ್ಯರು ಮತ್ತು/ಅಥವಾ ಅವರ ಸಹಾಯಕರು ಸಾಮಾನ್ಯ ಮತ್ತು ವಿಶೇಷ ಸೂಚನೆಗಳ ಅಡಿಯಲ್ಲಿ ಒಳರೋಗಿಗಳ ಆಧಾರದ ಮೇಲೆ ನನಗೆ ಮಾಡಬಹುದಾದ ಕಾರ್ಯವಿಧಾನಗಳು ಮತ್ತು ಸೇವೆಗಳಿಗೆ ನಾನು ಸ್ವಯಂಪ್ರೇರಿತವಾಗಿ ಸಮ್ಮತಿಸುತ್ತೇನೆ. ಈ ಕಾರ್ಯವಿಧಾನಗಳು ಮತ್ತು ಸೇವೆಗಳು ತುರ್ತು ಚಿಕಿತ್ಸೆ ಅಥವಾ ಪ್ರಯೋಗಾಲಯ ಕಾರ್ಯವಿಧಾನಗಳು, ಇಮೇಜಿಂಗ್ ಸೇವೆಗಳು, ವೈದ್ಯಕೀಯ ಅಥವಾ ಶಸ್ತ್ರ ಚಿಕಿತ್ಸೆ ಕಾರ್ಯವಿಧಾನಗಳು, ಅರಿವಳಿಕೆ ಅಥವಾ ಆಸ್ಪತ್ರೆ ಸೇವೆಗಳನ್ನು ಒಳಗೊಂಡಿರಬಹುದು ಆದರೆ ಅವುಗಳಿಗೆ ಸೀಮಿತವಾಗಿಲ್ಲ ಎಂದು ನಾನು ಅರ್ಥಮಾಡಿಕೊಂಡಿದ್ದೇನೆ. ಹೆಚ್ಚುವರಿ ಚಿಕಿತ್ಸೆಯ ಅಗತ್ಯವಿರುವ ಇತರ ಪರಿಸ್ಥಿತಿಗಳನ್ನು ರೋಗನಿರ್ಣಯ ಮಾಡಬಹುದು ಎಂದು ನಾನು ಅರ್ಥಮಾಡಿಕೊಂಡಿದ್ದೇನೆ.",

          fontSize: 10,
          font: "NotoSansKannada",

          margin: [0, 0, 0, 8],
        },

        {
          text: "ಯಾವುದೇ ಚಿಕಿತ್ಸೆಯ ಫಲಿತಾಂಶದ ಬಗ್ಗೆ ನನಗೆ ಯಾವುದೇ ಖಾತರಿ ನೀಡಲಾಗಿಲ್ಲ ಎಂದು ನಾನು ಒಪ್ಪಿಕೊಳ್ಳುತ್ತೇನೆ. ಈ ವಿಷಯಗಳನ್ನು ನನಗೆ ಅರ್ಥ ಆಗುವ ಭಾಷೆಯಲ್ಲಿ ನನಗೆ ಸಂಪೂರ್ಣವಾಗಿ ವಿವರಿಸಲಾಗಿದೆ ಮತ್ತು ನಾನು ಅದನ್ನು ಅರ್ಥಮಾಡಿಕೊಂಡಿದ್ದೇನೆ.",

          fontSize: 10,
          font: "NotoSansKannada",

          margin: [0, 0, 0, 8],
        },

        sigBlock("kn"),

        requiredGuardianWitnessTable("kn"),
      ],
    };
  };

  // ---------------------------------------------------------
  // PRINT
  // ---------------------------------------------------------
  const printConsent = () => {
    pdfMake.vfs = {
      ...pdfFonts.pdfMake.vfs,
      "NotoSansKannada-Regular.ttf": NotoSansKannada,
    };

    pdfMake.fonts = {
      Roboto: {
        normal: "Roboto-Regular.ttf",
        bold: "Roboto-Medium.ttf",
        italics: "Roboto-Italic.ttf",
        bolditalics: "Roboto-MediumItalic.ttf",
      },

      NotoSansKannada: {
        normal: "NotoSansKannada-Regular.ttf",
        bold: "NotoSansKannada-Regular.ttf",
      },
    };

    const docDefinition = buildDocDefinition();

    pdfMake.createPdf(docDefinition).getBlob((blob) => {
      const url = URL.createObjectURL(blob);

      window.open(
        `/Patient_Profile_Print?file=${encodeURIComponent(url)}`,
        "_blank"
      );
    });
  };

  return null;
}

export default PrintAdmissionConsent;
